import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Wrench, Eye, EyeSlash, CheckCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [showPw, setShowPw] = useState(false);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (pw !== confirm) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6 py-12">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2.5 mb-8">
          <img src="/apt-logo.jpg" alt="Apt Enterprises" className="h-6 w-auto" />
          <span className="font-bold tracking-tight text-slate-900">Tool Room Workshop ERP</span>
        </div>

        {done ? (
          <div className="space-y-6" data-testid="reset-done">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="h-6 w-6" weight="fill" />
              <span className="font-semibold">Password updated</span>
            </div>
            <p className="text-sm text-slate-500">Your password has been changed. You can now sign in with your new password.</p>
            <Button onClick={() => navigate("/login")} className="w-full bg-blue-600 hover:bg-blue-700 h-11" data-testid="reset-goto-login">Go to Sign In</Button>
          </div>
        ) : !token ? (
          <div className="space-y-4" data-testid="reset-invalid">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900">Invalid link</h2>
            <p className="text-sm text-slate-500">This password reset link is missing or invalid. Please request a new one from the sign-in page.</p>
            <Button onClick={() => navigate("/login")} variant="outline" className="w-full" data-testid="reset-back-login">Back to Sign In</Button>
          </div>
        ) : (
          <>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900">Set a new password</h2>
            <p className="text-slate-500 mt-1 mb-8">Choose a strong password you haven't used before.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="new-pw">New Password</Label>
                <div className="relative mt-1.5">
                  <Input id="new-pw" type={showPw ? "text" : "password"} className="bg-white pr-10" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 6 characters" required data-testid="reset-new-password" />
                  <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {showPw ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="confirm-pw">Confirm Password</Label>
                <Input id="confirm-pw" type={showPw ? "text" : "password"} className="mt-1.5 bg-white" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-confirm-password" />
              </div>
              {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="reset-error">{error}</div>}
              <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 h-11" data-testid="reset-submit">
                {loading ? "Please wait..." : "Update Password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
